import type { OcrCell, OcrRow } from "./ggb-types.js";

/** Row labels as printed on Golf GameBook scorecards (English + Nordic). */
export const aliases = {
  hole: ["hole", "hull", "hål", "hal"],
  si: ["handicap", "hcp", "slope", "idx", "index", "si"],
  par: ["par"],
  gross: ["score", "brutto", "gross", "slag"],
  net: ["net", "netto"],
  points: ["poeng", "points", "pts", "poäng", "pisteet"],
  out: ["out", "ut"],
  in: ["in", "inn", "i"],
  total: ["total", "totalt", "sum"],
} as const;

export type AnchorKey = "out" | "in" | "total";

/** Only the digits of an OCR token ("1|" -> "1", "Hole" -> ""). */
export function digitsOf(text: string): string {
  return String(text ?? "").replace(/[̀-ͯ]/g, "");
}

/** The small integer an OCR token stands for, or null when it is not one. */
export function toInt(text: string): number | null {
  const clean = String(text ?? "")
    .trim()
    .replace(/^[|[(:.\-–—]+/, "")
    .replace(/[|\]):.\-–—]+$/, "");
  return /^\d{1,3}$/.test(clean) ? Number(clean) : null;
}

/** Loose label match: exact, shared 4-letter prefix, or one edit away. */
export function fuzzy(
  word: string | undefined,
  list: readonly string[],
): boolean {
  const w = String(word ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-zåäöæø]/g, "");
  if (!w) return false;
  return list.some((alias) => {
    if (w === alias) return true;
    if (
      alias.length >= 4 &&
      (w.startsWith(alias.slice(0, 4)) || alias.startsWith(w.slice(0, 4)))
    ) {
      return true;
    }
    return editDistanceWithinOne(w, alias);
  });
}

function editDistanceWithinOne(w: string, alias: string): boolean {
  if (Math.abs(w.length - alias.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < w.length && j < alias.length) {
    if (w[i] === alias[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (w.length > alias.length) i += 1;
    else if (w.length < alias.length) j += 1;
    else {
      i += 1;
      j += 1;
    }
  }
  return edits + (w.length - i) + (alias.length - j) <= 1;
}

/** Out / In / Total anchor label; short aliases ("i", "ut") must be exact. */
export function anchorKey(label: string | undefined): AnchorKey | null {
  const w = String(label ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-zåäöæø]/g, "");
  if (!w) return null;
  for (const key of ["out", "in", "total"] as const) {
    const list: readonly string[] = aliases[key];
    if (list.includes(w)) return key;
    const long = list.filter((alias) => alias.length >= 3);
    if (w.length >= 3 && fuzzy(w, long)) return key;
  }
  return null;
}

/** Groups OCR cells into rows by vertical centre, sorted left to right. */
export function groupRows(cells: readonly OcrCell[]): OcrRow[] {
  const rows: OcrRow[] = [];
  const sorted = cells
    .filter((cell) => String(cell.text ?? "").trim() !== "")
    .slice()
    .sort((a, b) => a.y + a.h / 2 - (b.y + b.h / 2));
  for (const cell of sorted) {
    const cy = cell.y + cell.h / 2;
    const row = rows.find(
      (candidate) => Math.abs(candidate.cy - cy) < Math.max(10, cell.h * 0.6),
    );
    if (row) {
      row.cells.push(cell);
      row.cy = (row.cy * (row.cells.length - 1) + cy) / row.cells.length;
    } else {
      rows.push({ cy, cells: [cell] });
    }
  }
  for (const row of rows) row.cells.sort((a, b) => a.x - b.x);
  return rows;
}

export function rowText(row: OcrRow): string {
  return row.cells.map((cell) => cell.text).join(" ");
}

/** Accent- and case-insensitive name key for matching players. */
export function normalizeName(name: string | null | undefined): string {
  return String(name ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
